import {
    Box,
    Button,
    CircularProgress,
    LinearProgress,
    Theme,
    Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import LocationManager, { canLocate } from "./locationManager";
import { hoursToTimeString } from "./lib/time";
import { createStyles, withStyles, WithStyles } from "@mui/styles";
import LogoBox from "./prims/LogoBox";
import { useNavigate } from "react-router";
import { ControlState, externalStatus, LocationState } from "./types";

const useStyles = (theme: Theme) =>
    createStyles({
        root: {},
        fullWidthButton: {
            marginTop: theme.spacing(3),
            width: "100%",
        },
        centeredContent: {
            marginTop: "16px",
            marginBottom: "16px",
            textAlign: "center",
        },
        progressWrapper: {
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(2),
        },
        progressLabel: {
            display: "flex",
            justifyContent: "space-between",
            marginBottom: theme.spacing(0.5),
        },
    });

interface Props extends WithStyles<typeof useStyles> {
    // externalStatus: externalStatus | undefined;
    serverState: externalStatus;
    reCue: () => void;
    setupServer: () => void;
    locationManager: LocationManager | null;
}

function Welcome(props: Props){
    const n = useNavigate();

    const { classes, serverState, reCue, locationManager } = props;

    const [localConnected, setLocalConnected] = useState(false);
    const [checkingLocal, setCheckingLocal] = useState(false);
    const [localRedirected, setLocalRedirected] = useState(false);
    const reCueRef = useRef(reCue);
    const setupServerRef = useRef(props.setupServer);

    useEffect(() => {
        reCueRef.current = reCue;
    }, [reCue]);

    useEffect(() => {
        setupServerRef.current = props.setupServer;
    }, [props.setupServer]);

    useEffect(() => {
        if (canLocate() || localRedirected) {
            return;
        }

        let isMounted = true;

        const checkLocal = async () => {
            if (!isMounted) {
                return;
            }
            setCheckingLocal(true);
            try {
                const response = await fetch("/local", { cache: "no-store" });
                const text = await response.text();
                const connected = text.trim().toLowerCase() === "ok";
                if (!isMounted) {
                    return;
                }
                setLocalConnected(connected);
                if (connected && !localRedirected) {
                    setLocalRedirected(true);
                    setupServerRef.current?.();
                    reCueRef.current?.();
                }
            } catch (err) {
                if (isMounted) {
                    setLocalConnected(false);
                }
            } finally {
                if (isMounted) {
                    setCheckingLocal(false);
                }
            }
        };

        checkLocal();
        const intervalId = setInterval(checkLocal, 5000);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, [localRedirected]);

    const completed = [false, false, false];
    const errored = [false, false, false];

    let prompt = (
        <Box>No Content</Box>
    );

    if (!canLocate()) {
        errored[0] = true;

        gtag("event", "device_location_not_can_location", {
            event_category: "location",
            event_label: "Location Api not available",
        });

        prompt = (
            <Box m={2}>
                <Typography variant="h4" gutterBottom>
                    Connect to Play With Lights Wi-Fi
                </Typography>
                <Typography variant="body1" paragraph>
                    We can't access your device location. Connect to the <strong>"Play With Lights"</strong> Wi-Fi network to continue. Once you're connected locally, we'll put you right in line automatically.
                </Typography>
                <Box className={classes.progressWrapper}>
                    {checkingLocal ? (
                        <LinearProgress color="secondary" />
                    ) : (
                        <LinearProgress
                            color="secondary"
                            variant={localConnected ? "determinate" : "indeterminate"}
                            value={localConnected ? 100 : undefined}
                        />
                    )}
                    <Typography
                        variant="body2"
                        color={localConnected ? "primary" : "textSecondary"}
                        mt={1}
                    >
                        {localConnected
                            ? "Connected locally! Sending you to the line..."
                            : "Waiting for a connection to the Play With Lights Wi-Fi..."}
                    </Typography>
                </Box>
                <Button
                    className={classes.fullWidthButton}
                    variant="contained"
                    color="primary"
                    onClick={() => window.open("https://playwithlights.com/faq#wifi", "_blank")}
                >
                    Wifi instructions
                </Button>
            </Box>
        );
    } else {
        completed[0] = true;
        completed[0] = true;

        if (serverState?.state === LocationState.Valid) {
            completed[1] = true;


            if (serverState.active && serverState.control_state === ControlState.Active) {
                completed[3] = true;
                prompt = <Box m={2}>Connected</Box>;
            } else if (!serverState.active) {
                completed[1] = true;

                const timeString = hoursToTimeString(serverState.times);

                gtag("event", "outside_time", {
                    event_category: "time",
                    event_label: "Outside Time Window",
                });

                prompt = (
                    <Box>
                        <h2>The lights are off right now. :(</h2>
                            You're close to the display at <b>Berolina</b>. The lights are off,
                            but know that we deeply respect the night owl in you.
                        <Box className={classes.centeredContent}>
                                The schedule is {timeString}.
                        </Box>
                        <Button
                            className={classes.fullWidthButton}
                            variant="contained"
                            onClick={() => n("/locations")}
                        >
Location Details
                        </Button>
                    </Box>
                );
            } else if (serverState.position === -2) {
                gtag("event", "still_there", {
                    event_category: "active",
                    event_label: "Still there?",
                });

                completed[1] = true;

                prompt = (
                    <Box>
                        <h2>Ya still there?</h2>
                            We haven't noticed any of your amazing designs lighting up the
                            night with glory and wonder for awhile.
                        <Button
                            className={classes.fullWidthButton}
                            variant="contained"
                            onClick={reCue}
                            style={{ marginTop: "24px" }}
                        >
                                I'm here! I'm here!
                        </Button>
                    </Box>
                );
            } else if (
                serverState.active && serverState.control_state === ControlState.InLine
            ) {
                gtag("event", "in_line", {
                    event_category: "active",
                    event_label: "In line",
                });

                const totalSlots = Math.max((serverState.line_length ?? 0) - 1, 0);
                const position = Math.max(serverState.position ?? 0, 0);
                const slotsBehind = Math.max(totalSlots - position, 0);
                const limitSeconds = serverState.control_limit?.LimitSeconds ?? 0;
                const secondsRemaining = serverState.seconds_remaining ?? undefined;
                const slotDuration = limitSeconds > 0 ? limitSeconds : secondsRemaining ?? 0;
                const waitSeconds =
                    slotDuration > 0 && secondsRemaining !== undefined
                        ? secondsRemaining + position * slotDuration
                        : undefined;

                prompt = (
                    <Box className={classes.centeredContent}>
                        <Typography variant="h5" gutterBottom>
                            You're in line at position {position} of {totalSlots}.
                        </Typography>
                        <Box className={classes.progressLabel}>
                            <Typography variant="body2" color="textSecondary">
                                Slots ahead: {Math.max(position, 0)}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Behind you: {Math.max(slotsBehind, 0)}
                            </Typography>
                        </Box>
                        {waitSeconds !== undefined && (
                            <Typography variant="body2" color="textSecondary" mt={1}>
                                About {waitSeconds}s until it's your turn
                            </Typography>
                        )}
                        <Box>
                            {serverState.control_limit?.LimitName
                                ? <>Everyone gets {serverState.control_limit?.LimitName}, so get those twinkle fingers ready!</>
                                : "Your twinkle turn is coming up soon—get ready!"}
                        </Box>
                    </Box>
                );
            } else if (serverState.position === -1) {
                gtag("event", "out_of_time", {
                    event_category: "active",
                    event_label: "Someone elses turn",
                });

                prompt = (
                    <Box>
                        <h1>
                                It's someone else's turn now... But we really liked your designs!)
                        </h1>
                        <p>
                                Someone else is in line behind you, and it's now their turn. If
                                you'd like to play again, you can get back in line!
                        </p>

                        <Button
                            className={classes.fullWidthButton}
                            variant="contained"
                            onClick={reCue}
                        >
                                Get back in line
                        </Button>
                    </Box>
                );
            } else {
                prompt = (
                    <Box>
                        <CircularProgress color="primary" />
                    </Box>
                );
            }
        } else if (serverState?.state === LocationState.TooFar) {
            gtag("event", "location_tooFar", {
                event_category: "location",
                event_label: "Too far away",
            });

            completed[0] = true;
            errored[1] = true;


            prompt = (
                <Box>
                    <h2>No Controllable Lights Nearby</h2>
                        Looks like you're too far away to control any lights from your
                        current location. This could be a temporary outage with a specific
                        location.

                    <Button
                        className={classes.fullWidthButton}
                        variant="contained"
                        onClick={() => n("/locations")}
                        style={{ marginTop: "24px" }}
                    >
                            Find other displays
                    </Button>

                </Box>
            );
        } else {
            if(locationManager){
                locationManager.reconnect();
            }

            gtag("event", "location_pending", {
                event_category: "location",
                event_label: "Waiting for location data",
            });
            prompt = (
                <Box alignContent="center">
                    <h2>Connecting</h2>
                        We're waiting for your device to send us location data.... Sometimes this can take a minute, and sometimes you just need to refresh.
                    <Box marginTop="24px">
                        <LinearProgress color="secondary" />
                    </Box>
                </Box>
            );
        }
    }

    return (
        <LogoBox>
            {prompt}
        </LogoBox>
    );
    
}

export default withStyles(useStyles, { withTheme: true })(Welcome);
